import { Logs } from '../services/Logs.js';
import { Notification } from '../services/Notification.js';

export class UIFormElement {

    constructor(options = {}) {
        if (!options.app) {
            throw new Error("UIFormElement requires an app option");
        }

        if (!options.container) {
            throw new Error("UIFormElement requires a container option");
        }

        if (!options.data) {
            throw new Error("UIFormElement requires a data option");
        }

        this.app = options.app;
        this.container = options.container;
        this.data = this.normalizeFormData(options.data);
        this.mode = options.mode;
        this.elementId = options.elementId;
        this.idModal = options.idModal;

        this.render();

        return this;
    }

    get config() {
        return this.app.config || {};
    }

    get edit() {
        return this.app.edit || {};
    }

    get tmp() {
        if (!this.app.tmp) this.app.tmp = {};
        return this.app.tmp;
    }

    randomString() {
        if (this.app && typeof this.app.randomString === "function") {
            return this.app.randomString();
        }

        return `-${Math.random().toString(36).slice(2, 10)}`;
    }

    getUploadUrl(path = "") {
        new Logs("message", ["UI FORM ELEMENT GET UPLOAD URL", path]);

        if (this.app && typeof this.app.getUploadUrl === "function") {
            return this.app.getUploadUrl(path);
        }

        const base = (this.config.upload_url || "/uploads").replace(/\/+$/, "");
        const cleanPath = String(path || "").replace(/^\/+/, "");

        return cleanPath ? `${base}/${cleanPath}` : base;
    }

    getTarget(container) {
        const target = typeof container === "string"
            ? document.querySelector(container)
            : container;

        if (!target) {
            new Logs("error", ["UIFormElement container not found", container]);
            return null;
        }

        return target;
    }

    getInputId(data) {
        return data.id || this.randomString();
    }

    appendLabel(target, data, inputId) {
        if (!data.label) return null;

        const label = document.createElement("label");
        label.classList.add("ui-input-label");
        label.htmlFor = inputId;
        label.textContent = data.label;
        target.append(label);

        return label;
    }

    createInput(data, options = {}) {
        const input = document.createElement("input");
        input.type = options.type || data.type || "text";
        input.id = options.id || this.getInputId(data);
        input.autocomplete = options.autocomplete || data.autocomplete || "off";

        input.classList.add("ui-input");
        if (options.classes) input.classList.add(...options.classes.filter(Boolean));
        if (data.class) input.classList.add(...data.class.split(" ").filter(Boolean));

        if (data.name) input.name = data.name;
        if (data.placeholder) input.placeholder = data.placeholder;
        if (data.disabled) input.disabled = true;
        if (data.readonly) input.readOnly = true;
        if (data.required) input.required = true;
        if (data.min !== null && typeof data.min !== "undefined") input.min = data.min;
        if (data.max !== null && typeof data.max !== "undefined") input.max = data.max;
        if (data.step !== null && typeof data.step !== "undefined") input.step = data.step;

        const value = this.getInputValue(data);
        if (value !== null && typeof value !== "undefined") input.value = value;
        this.bindValueState(input);

        return input;
    }

    getInputValue(data) {
        if (data.value !== null && typeof data.value !== "undefined") return data.value;
        if (data.default_value !== null && typeof data.default_value !== "undefined") return data.default_value;
        return null;
    }

    normalizeFormData(data) {
        new Logs("message", ["UI FORM ELEMENT NORMALIZE FORM DATA", data]);

        if (!data || typeof data !== "object") {
            return data;
        }

        if (typeof data.onChange === "string") {
            data.onChange = this.resolveAction(data.onChange, "onChange");
        }

        if (typeof data.values === "string") {
            data.values = this.resolveValue(data.values);
        }

        return data;
    }

    resolveAction(action, property) {
        new Logs("message", ["UI FORM ELEMENT RESOLVE ACTION", property, action]);

        if (typeof action === "function") {
            return action;
        }

        if (typeof action !== "string") {
            return null;
        }

        const match = action.trim().match(/^([A-Za-z_$][\w$]*)\((.*)\)$/);
        if (!match) {
            new Logs("warning", ["Form action format is not supported", property, action]);
            return null;
        }

        const method = match[1];
        const args = this.parseActionArguments(match[2]);
        const context = this.getActionContext(method);

        if (!context || typeof context[method] !== "function") {
            new Logs("warning", ["Form action method is not available", property, method]);
            return null;
        }

        return (event) => context[method](...args.map((arg) => this.resolveActionArgument(arg, event)));
    }

    getActionContext(method) {
        new Logs("message", ["UI FORM ELEMENT GET ACTION CONTEXT", method]);

        if (this.app && typeof this.app[method] === "function") {
            return this.app;
        }

        if (typeof this[method] === "function") {
            return this;
        }

        return null;
    }

    parseActionArguments(args) {
        new Logs("message", ["UI FORM ELEMENT PARSE ACTION ARGUMENTS", args]);

        const value = args.trim();
        if (!value) {
            return [];
        }

        return value.split(",").map((arg) => arg.trim());
    }

    resolveActionArgument(argument, event) {
        new Logs("message", ["UI FORM ELEMENT RESOLVE ACTION ARGUMENT", argument]);

        if (argument === "e" || argument === "event") return event;
        if (argument === "true") return true;
        if (argument === "false") return false;
        if (argument === "null") return null;
        if (argument === "undefined") return undefined;
        if (/^-?\d+(\.\d+)?$/.test(argument)) return Number(argument);

        const quoted = argument.match(/^["'](.*)["']$/);
        if (quoted) return quoted[1];

        if (argument.startsWith("this.")) {
            return this.resolveValue(argument);
        }

        return argument;
    }

    resolveValue(expression) {
        new Logs("message", ["UI FORM ELEMENT RESOLVE VALUE", expression]);

        if (typeof expression !== "string") {
            return expression;
        }

        const path = expression.trim();
        if (!/^this(?:\.[A-Za-z_$][\w$]*)+$/.test(path)) {
            new Logs("warning", ["Form value format is not supported", expression]);
            return null;
        }

        const keys = path.replace(/^this\./, "").split(".");
        const value = this.resolvePath(keys);

        if (typeof value === "undefined") {
            new Logs("warning", ["Form value path is not available", expression]);
            return null;
        }

        return value;
    }

    resolvePath(keys) {
        new Logs("message", ["UI FORM ELEMENT RESOLVE PATH", keys]);

        const fromElement = this.getPathValue(this, keys);
        if (typeof fromElement !== "undefined") {
            return fromElement;
        }

        const fromApp = this.getPathValue(this.app, keys);
        if (typeof fromApp !== "undefined") {
            return fromApp;
        }

        if (keys.length === 1) {
            return this.getPathValue(this.app?.config, keys);
        }

        return undefined;
    }

    getPathValue(source, keys) {
        new Logs("message", ["UI FORM ELEMENT GET PATH VALUE", keys]);

        if (!source) {
            return undefined;
        }

        return keys.reduce((current, key) => {
            if (current === null || typeof current === "undefined") {
                return undefined;
            }

            return current[key];
        }, source);
    }

    formatFileSize(size) {
        if (!Number.isFinite(size)) return "";
        if (size === 0) return "0 octet";

        const units = ["octets", "Ko", "Mo", "Go", "To"];
        const exponent = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
        const value = size / (1024 ** exponent);
        const precision = exponent === 0 || value >= 10 ? 0 : 1;

        return `${value.toFixed(precision).replace(".", ",")} ${units[exponent]}`;
    }

    bindInputChange(input, data) {
        if (typeof data.onChange === "function") {
            input.addEventListener("change", (event) => data.onChange(event));
        }

        if (typeof data.focusOut === "function") {
            input.addEventListener("focusout", (event) => data.focusOut(event));
        }
    }

    bindValueState(input) {
        if (!input || ["hidden", "file"].includes(input.type)) return;

        const sync = () => {
            input.classList.toggle("has-value", String(input.value || "").trim() !== "");
        };

        input.addEventListener("input", sync);
        input.addEventListener("change", sync);
        queueMicrotask(sync);
    }

    appendUnit(target, data) {
        if (!data.unit) return null;

        const unit = document.createElement("div");
        unit.classList.add("unit");
        unit.textContent = data.unit;
        target.append(unit);

        return unit;
    }

    getCheckboxSideText(data, checked = false) {
        if (Array.isArray(data.sidetext)) {
            return checked ? data.sidetext[1] : data.sidetext[0];
        }

        if (typeof data.sidetext === "string") {
            return data.sidetext;
        }

        return null;
    }

    UIModalTurn(...args) {
        if (this.app && typeof this.app.UIModalTurn === "function") return this.app.UIModalTurn(...args);
        return false;
    }

    DrawExit(...args) {
        if (this.app && typeof this.app.DrawExit === "function") return this.app.DrawExit(...args);
        return false;
    }

    drawRando(...args) {
        if (this.app && typeof this.app.drawRando === "function") return this.app.drawRando(...args);
        return false;
    }

    setClickMode(...args) {
        if (this.app && typeof this.app.setClickMode === "function") return this.app.setClickMode(...args);
        return false;
    }

    renderEspecesCustomList(...args) {
        if (this.app && typeof this.app.renderEspecesCustomList === "function") return this.app.renderEspecesCustomList(...args);
        return false;
    }

    addEspeceToCustomList(...args) {
        if (this.app && typeof this.app.addEspeceToCustomList === "function") return this.app.addEspeceToCustomList(...args);
        return false;
    }

    render() {
        const element = this.appendElementContainer();
        if (!element) return false;

        return this.renderElement(element, this.data, element.id);
    }

    appendElementContainer() {
        const target = this.getTarget(this.container);
        if (!target) return false;

        const element = target.classList.contains("ui-form-element")
            ? target
            : document.createElement("div");

        if (!element.id) {
            element.id = this.elementId || this.data.element_id || this.randomString();
        }

        element.classList.add("ui-form-element");

        if (this.data.class) {
            element.classList.add(...this.data.class.split(" ").filter(Boolean));
        }

        if (this.data.type === "hidden") {
            element.classList.add("hidden");
        }

        if (element !== target) {
            target.append(element);
        }

        return element;
    }

    renderElement(container, data, elementId) {
        new Logs("message", ["RENDER FORM ELEMENT", container, data, this.mode, elementId, this.idModal]);

        const renderers = {
            message: () => this.UIInputMessage(container, data),
            separator: () => this.UIFormSeparator(container, data),
            hidden: () => this.UIInputHidden(container, data),
            text: () => this.UIInputText(container, data),
            number: () => this.UIInputNumber(container, data),
            password: () => this.UIInputPassword(container, data),
            tel: () => this.UIInputTel(container, data),
            url: () => this.UIInputUrl(container, data),
            email: () => this.UIInputEmail(container, data),
            validation_code: () => this.UIInputValidationCode(container, data),
            textarea: () => this.UIInputTextArea(container, data),
            select: () => this.UIInputSelect(container, data),
            select_multi: () => this.UIInputSelectMulti(container, data, elementId),
            date: () => this.UIInputDate(container, data),
            time: () => this.UIInputTime(container, data),
            checkbox: () => this.UIInputCheckbox(container, data),
            file: () => this.UIInputFile(container, data),
            photo: () => this.UIInputPhoto(container, data),
            photo_simple: () => this.UIInputPhotoSimple(container, data),
            photo_profil: () => this.UIInputPhotoProfil(container, data),
            video: () => this.UIInputVideo(container, data),
            location: () => this.UIInputLocation(container, data, this.mode, this.idModal),
            range: () => this.UIInputRange(container, data),
            route_point: () => this.UIInputRoutePoint(container, data),
            inverter: () => this.UIInputInverter(container, data)
        };

        if (!renderers[data.type]) {
            new Logs("warning", ["Unknown UIFormElement type", data.type]);
            return false;
        }

        return renderers[data.type]();
    }

UIFormSeparator(container, data) {

    new Logs("message", ["FORM BUILDER SEPARATOR", data, container]);

    // INIT
    const id = this.randomString();

    // SET CONTENT
    $(container).append(`<div class="ui-input-separator" id="${id}"></div>`);

    // ADD CLASS
    if (data.class) $(`#${id}`).addClass(data.class)

}

/* --------------------------------------------
                  INPUTS
  ---------------------------------------------*/

// MESSAGE
UIInputMessage(container, data) {

    new Logs("message", ["FORM BUILDER MESSAGE", data, container]);

    // SET CONTENT
    $(container).append(`<div class="ui-form-element-message">${data.text}</div>`);

}

// ROUTE POINT
UIInputRoutePoint(container, data) {

    new Logs("message", ["FORM BUILDER ROUTE POINT", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const label = document.createElement("div");
    label.classList.add("label");
    label.textContent = data.label || "";
    target.append(label);

    const input = document.createElement("input");
    input.type = "text";
    input.name = data.name || data.mode || "";
    input.autocomplete = data.autocomplete || "off";
    input.placeholder = data.placeholder || "";
    input.classList.add("ui-input", "name");
    target.append(input);

    target.append(this.createRoutePointResults());
    target.append(this.createRoutePointMenu(data));

    const waiter = document.createElement("div");
    waiter.classList.add("waiter");

    const ball = document.createElement("div");
    ball.classList.add("hankBall");
    waiter.append(ball, document.createTextNode("Recherche en cours..."));
    target.append(waiter);

    return target;

}

createRoutePointResults() {

    new Logs("message", ["FORM BUILDER CREATE ROUTE POINT RESULTS"]);

    const results = document.createElement("div");
    results.classList.add("results");

    const close = document.createElement("div");
    close.classList.add("close", "glass-icon");

    const searching = document.createElement("div");
    searching.classList.add("searching");

    const ball = document.createElement("div");
    ball.classList.add("hankBall");

    const searchingText = document.createElement("div");
    searchingText.textContent = "Recherche en cours";
    searching.append(ball, searchingText);

    const noResult = document.createElement("div");
    noResult.classList.add("no-result");
    noResult.textContent = "Aucune adresse trouvée";

    const list = document.createElement("div");
    list.classList.add("list");

    results.append(close, searching, noResult, list);

    return results;

}

createRoutePointMenu(data) {

    new Logs("message", ["FORM BUILDER CREATE ROUTE POINT MENU", data]);

    const menu = document.createElement("div");
    menu.classList.add("route-point-menu");

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.classList.add("ui-buttons", "icon-only", "square", "route-point-menu-toggle");
    toggle.setAttribute("aria-label", "Options");

    const toggleIcon = document.createElement("img");
    toggleIcon.src = "/images/menu.svg";
    toggleIcon.alt = "";
    toggle.append(toggleIcon);

    const list = document.createElement("div");
    list.classList.add("route-point-menu-list");
    list.append(
        this.createRoutePointMenuItem({
            id: data.locatorMapId,
            icon: "target-grey.svg",
            label: "Choisir sur la carte"
        }),
        this.createRoutePointMenuItem({
            id: data.locatorMeId,
            icon: "locate-grey.svg",
            label: "Ma position actuelle"
        })
    );

    list.addEventListener("click", () => {
        menu.classList.remove("open");
    });

    toggle.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const shouldOpen = !menu.classList.contains("open");
        this.closeRoutePointMenus();
        if (shouldOpen) menu.classList.add("open");
    });

    this.bindRoutePointMenuDocumentClose();

    menu.append(toggle, list);

    return menu;

}

bindRoutePointMenuDocumentClose() {

    new Logs("message", ["FORM BUILDER BIND ROUTE POINT MENU DOCUMENT CLOSE"]);

    if (UIFormElement.routePointMenuDocumentCloseBound) {
        return true;
    }

    document.addEventListener("click", (event) => {
        if (event.target.closest(".route-point-menu")) {
            return;
        }

        this.closeRoutePointMenus();
    }, true);

    UIFormElement.routePointMenuDocumentCloseBound = true;
    return true;

}

closeRoutePointMenus() {

    new Logs("message", ["FORM BUILDER CLOSE ROUTE POINT MENUS"]);

    document.querySelectorAll(".route-point-menu.open").forEach((menu) => {
        menu.classList.remove("open");
    });

}

createRoutePointMenuItem(options) {

    new Logs("message", ["FORM BUILDER CREATE ROUTE POINT MENU ITEM", options]);

    const button = document.createElement("div");
    button.id = options.id;
    button.classList.add("route-point-menu-item");

    const icon = document.createElement("img");
    icon.src = `/images/${options.icon}`;
    icon.alt = "";
    button.append(icon, document.createTextNode(options.label));

    return button;

}

// INVERTER
UIInputInverter(container, data) {

    new Logs("message", ["FORM BUILDER INVERTER", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const inverter = document.createElement("div");
    inverter.classList.add("inverter");

    const icon = document.createElement("img");
    icon.src = "/images/inverse-grey.svg";
    icon.classList.add("glass-icon");
    icon.alt = "";
    inverter.append(icon);
    target.append(inverter);

    return inverter;

}

// HIDDEN
UIInputHidden(container, data) {

    new Logs("message", ["FORM BUILDER HIDDEN", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "hidden"
    });

    target.append(input);

    return input;

}

// FILE
UIInputFile(container, data) {

    new Logs("message", ["FORM BUILDER FILE", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "file",
        classes: ["ui-input-file-native"]
    });

    if (Array.isArray(data.accept)) {
        input.accept = data.accept.join(",");
    } else if (typeof data.accept === "string") {
        input.accept = data.accept;
    } else if (Array.isArray(data.accepted_files)) {
        input.accept = data.accepted_files.join(",");
    } else if (typeof data.accepted_files === "string") {
        input.accept = data.accepted_files;
    }

    this.appendLabel(target, data, input.id);
    target.append(input);

    const trigger = document.createElement("div");
    trigger.classList.add("ui-input", "ui-input-file-button");
    trigger.setAttribute("role", "button");
    trigger.setAttribute("tabindex", "0");
    trigger.setAttribute("aria-controls", input.id);

    const icon = document.createElement("img");
    icon.src = "/images/download-grey.svg";
    icon.alt = "";
    icon.setAttribute("aria-hidden", "true");

    const text = document.createElement("span");
    text.textContent = data.text || "Cliquez pour ajouter votre fichier...";

    const fileName = document.createElement("span");
    fileName.classList.add("ui-input-file-name");
    fileName.append(text);

    const clear = document.createElement("img");
    clear.classList.add("ui-input-file-clear");
    clear.src = "/images/trash-grey.svg";
    clear.alt = "Vider le champ";
    clear.title = "Vider le champ";
    clear.hidden = true;

    const fileInfo = document.createElement("span");
    fileInfo.classList.add("ui-input-file-info");
    fileInfo.append(fileName);

    const size = document.createElement("span");
    size.classList.add("ui-input-file-size");

    trigger.append(icon, fileInfo);
    target.append(trigger);

    trigger.addEventListener("click", () => {
        input.click();
    });

    trigger.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        input.click();
    });

    clear.addEventListener("click", (event) => {
        event.stopPropagation();
        input.value = "";
        text.textContent = data.text || "Cliquez pour ajouter votre fichier...";
        size.remove();
        clear.hidden = true;
    });

    input.addEventListener("change", () => {
        const file = input.files[0];

        if (!file) {
            text.textContent = data.text || "Cliquez pour ajouter votre fichier...";
            size.remove();
            clear.hidden = true;
            return;
        }

        text.textContent = file.name;
        fileName.append(clear);
        clear.hidden = false;
        size.textContent = this.formatFileSize(file.size);
        fileInfo.append(size);
    });

    return input;

}

// PHOTO SIMPLE
UIInputPhotoSimple(container, data) {

    new Logs("message", ["UI INPUT PHOTO SIMPLE", container, data]);

    return this.UIInputPhotoSimpleVariant(container, data, {
        containerClass: "ui-input-photo-container-simple",
        instanceClass: "ui-input-photo-instance-simple",
        withLabel: true
    });

}

// PHOTO PROFIL
UIInputPhotoProfil(container, data) {

    new Logs("message", ["UI INPUT PHOTO PROFIL", container, data]);

    return this.UIInputPhotoSimpleVariant(container, data, {
        containerClass: "ui-input-photo-container-profil",
        instanceClass: "ui-input-photo-instance-profil",
        withLabel: false
    });

}

UIInputPhotoSimpleVariant(container, data, options = {}) {

    new Logs("message", ["UI INPUT PHOTO SIMPLE VARIANT", container, data, options]);

    const target = this.getTarget(container);
    if (!target) return false;

    target.classList.add(options.containerClass || "ui-input-photo-container-simple");
    target.innerHTML = "";

    if (options.withLabel !== false && data.label) {
        const label = document.createElement("label");
        label.classList.add("ui-input-label");
        label.textContent = data.label;
        target.append(label);
    }

    // SET INPUT IDs
    let id = this.randomString()
    if(data.id) id = data.id
    const id_instance = id + "-instance"
    const id_preview = id + "-preview"
    const id_status = id + "-status"

    const instance = document.createElement("div");
    instance.classList.add(options.instanceClass || "ui-input-photo-instance-simple");
    instance.id = id_instance;

    const preview = document.createElement("div");
    preview.classList.add("ui-input-photo-preview");
    preview.id = id_preview;
    instance.append(preview);

    const trash = document.createElement("div");
    trash.classList.add("ui-input-photo-btn", "ui-input-photo-btn-trash");
    instance.append(trash);

    const statusInput = document.createElement("input");
    statusInput.type = "hidden";
    statusInput.id = id_status;
    statusInput.name = id_status;
    statusInput.value = "empty";
    if (data.form) statusInput.setAttribute("form", data.form);
    instance.append(statusInput);

    const fileInput = document.createElement("input");
    fileInput.type = "hidden";
    fileInput.classList.add("ui-input-photo-file");
    fileInput.id = id;
    if (data.name) fileInput.name = data.name;
    if (data.form) fileInput.setAttribute("form", data.form);
    fileInput.style.opacity = "0";
    fileInput.style.pointerEvents = "none";
    instance.append(fileInput);
    target.append(instance);

    // ADD TRASH EVENT
    trash.addEventListener("click", () => {

        // SET HIDDEN VALUES
        statusInput.type = "hidden";
        statusInput.value = "removed";
        fileInput.type = "hidden";
        fileInput.value = "";

        // RESET PREVIEW
        instance.classList.remove("set");
        preview.style.backgroundImage = "";
        preview.style.backgroundSize = "";

    })

    // ADD PREVIEW CLICK EVENT
    preview.addEventListener("click", () => {
        fileInput.type = "file";
        fileInput.focus();
        fileInput.click();
    })

    // ADD INPUT CHANGE EVENT
    fileInput.addEventListener("change", () => {
        const reader = new FileReader()
        const file = fileInput.files[0]
        console.log("File", file)
        console.log("accepted_files", data.accepted_files)

        if (!file) return false;

        // CHECK FILE
        if (!data.accepted_files.includes(file.type)) {
            new Notification({
                title: "Attention",
                content: "Désolé, ce type de fichier n'est pas accepté.",
                icon: "warning.svg"
            });
            return false
        }

        // SET READER DATA
        reader.readAsDataURL(file)
        reader.onload = function () {

            // SET SET ;-)
            instance.classList.add("set");
            statusInput.type = "hidden";
            statusInput.value = "modify";

            // SET BACKGROUND TO PREVIEW
            preview.style.backgroundImage = `url(${this.result})`;
            preview.style.backgroundSize = "cover";

        };

    })

    // SET CONTENT IF EXISTS
    if (data.value && data.value.id_medias && data.value.extension) {

        const media_file = this.getUploadUrl(`${data.value.id_medias}.${data.value.extension}`)

        // SET SET ;-)
        instance.classList.add("set");

        // SET BACKGROUND TO PREVIEW
        preview.style.backgroundImage = `url(${media_file})`;
        preview.style.backgroundSize = "cover";

        // SET NO CHANGE
        statusInput.type = "hidden";
        statusInput.value = "unchanged";

    } else {

        // SET BTN TRASH INVISIBLE
        trash.classList.remove("visible");

    }


}

// PHOTO
UIInputPhoto(container, data) {

    new Logs("message", ["UI INPUT PHOTO", container, data]);

    const target = this.getTarget(container);
    if (!target) return false;

    target.classList.add("ui-input-photo-container");
    target.innerHTML = "";

    // SET LABEL
    const label = document.createElement("label");
    label.classList.add("ui-input-label");
    if (data.label) {
        label.textContent = data.label;
    } else {
        if(data.max > 1) {
            label.textContent = "Cliquez sur les emplacements pour ajouter une ou plusieurs photos.";
        } else {
            label.textContent = "Cliquez sur l'emplacement pour ajouter votre photo.";
        }
    }
    target.append(label);

    // ADD INSTANCE
    if(!data.value || data.value.length === 0) {
        for(let i= 0; i < data.max; i++) {
            this.UIInputPhotoInstance(target, data.name, data.accepted_files, null, data.sphere)
        }
    } else {
        data.value.forEach((photo) => {
            this.UIInputPhotoInstance(target, data.name, data.accepted_files, photo, data.sphere)
        })

        // ADD EMPTY INSTANCE
        if(data.max > data.value.length) {
            for(let i=0; i < data.max - data.value.length; i++) {
                this.UIInputPhotoInstance(target, data.name, data.accepted_files, null, data.sphere)
            }
        }
    }

    // ADD REQUIRED
    if(data.required) {
        // TODO AJouter une possibilité de rendre require pas JS
    }



}

// PHOTO INSTANCE
UIInputPhotoInstance(container, name, accepted_files, photo, sphere) {

        new Logs("message", ["UI INPUT PHOTO INSTANCE", container, name, accepted_files, photo, sphere]);

        const target = this.getTarget(container);
        if (!target) return false;

        // SET INPUT IDs
        let id = this.randomString()
        if(photo && photo.id_medias) id = photo.id_medias
        const id_instance = name + "-" + id
        const id_preview = this.randomString()
        const id_file = id_instance + "-file"
        const id_status = id_instance + "-status"
        const id_sphere = id_instance + "-sphere"


        // CREATE CONTAINER
        const instance = document.createElement("div");
        instance.classList.add("ui-input-photo-instance");
        instance.id = id_instance;

        // ADD PREVIEW
        const preview = document.createElement("div");
        preview.classList.add("ui-input-photo-preview");
        preview.id = id_preview;
        instance.append(preview);

        // ADD TRASH
        const trash = document.createElement("div");
        trash.classList.add("ui-input-photo-btn", "ui-input-photo-btn-trash");
        instance.append(trash);

        // ADD SPHERE
        let sphereButton = null;
        if(sphere) {
            sphereButton = document.createElement("div");
            sphereButton.classList.add("ui-input-photo-btn", "ui-input-photo-btn-sphere");
            instance.append(sphereButton);
        }

        const statusInput = document.createElement("input");
        statusInput.type = "hidden";
        statusInput.classList.add("ui-input");
        statusInput.id = id_status;
        statusInput.name = id_status;
        statusInput.value = "empty";
        instance.append(statusInput);

        const sphereInput = document.createElement("input");
        sphereInput.type = "hidden";
        sphereInput.classList.add("ui-input");
        sphereInput.id = id_sphere;
        sphereInput.name = id_sphere;
        sphereInput.value = "0";
        instance.append(sphereInput);

        const fileInput = document.createElement("input");
        fileInput.type = "hidden";
        fileInput.classList.add("ui-input", "ui-input-photo-file");
        fileInput.id = id_file;
        fileInput.name = id_file;
        fileInput.style.opacity = "0";
        fileInput.style.pointerEvents = "none";
        instance.append(fileInput);
        target.append(instance);

        // ADD TRASH EVENT
        trash.addEventListener("click", () => {

            // SET HIDDEN VALUES
            statusInput.type = "hidden";
            statusInput.value = "removed";
            fileInput.type = "hidden";
            fileInput.value = "";
            sphereInput.value = "0";

            // RESET PREVIEW
            instance.classList.remove("set");
            preview.style.backgroundImage = "";
            preview.style.backgroundSize = "";
            if (sphereButton) sphereButton.classList.remove("ui-input-photo-btn-sphere-on");

        })

        // ADD SPHERE EVENT
        if (sphereButton) sphereButton.addEventListener("click", () => {

            statusInput.value = "modify";

            if(sphereInput.value === "1") {
                sphereButton.classList.remove("ui-input-photo-btn-sphere-on");
                sphereInput.value = "0";

            } else {
                sphereButton.classList.add("ui-input-photo-btn-sphere-on");
                sphereInput.value = "1";
            }

        })

        // ADD PREVIEW CLICK EVENT
        preview.addEventListener("click", () => {
            console.log("PREVIEW CLICKED")
            fileInput.type = "file";
            fileInput.focus();
            fileInput.click();
        })

        // ADD INPUT CHANGE EVENT
        fileInput.addEventListener("change", () => {
            const reader = new FileReader()
            const file = fileInput.files[0]
            console.log("File", file)
            console.log("accepted_files", accepted_files)

            if (!file) return false;

            // CHECK FILE
            if (!accepted_files.includes(file.type)) {
                new Notification({
                    title: "Attention",
                    content: "Désolé, ce type de fichier n'est pas accepté.",
                    icon: "warning.svg"
                });
                return false
            }

            // SET READER DATA
            reader.readAsDataURL(file)
            reader.onload = function () {

                // SET SET ;-)
                instance.classList.add("set");
                statusInput.type = "hidden";
                statusInput.value = "modify";

                // SET BACKGROUND TO PREVIEW
                preview.style.backgroundImage = `url(${this.result})`;
                preview.style.backgroundSize = "cover";

            };

        })

        // SET CONTENT IF EXISTS
        if (photo && photo.id_medias && photo.extension) {

            const media_file = this.getUploadUrl(`${photo.id_medias}.${photo.extension}`)

            // SET SET ;-)
            instance.classList.add("set");

            // SET BACKGROUND TO PREVIEW
            preview.style.backgroundImage = `url(${media_file})`;
            preview.style.backgroundSize = "cover";

            // SET SPHERE
            if(photo.sphere === 1) {
                if (sphereButton) sphereButton.classList.add("ui-input-photo-btn-sphere-on");
                sphereInput.value = "1";
            }

            // SET NO CHANGE
            statusInput.type = "hidden";
            statusInput.value = "unchanged";


        } else {

            // SET BTN TRASH INVISIBLE
            trash.classList.remove("visible");

        }

}

// VIDEO
UIInputVideo(container, data) {

    new Logs("message", ["UI INPUT VIDEO", container, data]);

    const target = this.getTarget(container);
    if (!target) return false;

    // ADD MEDIA CLASS TO SUBITEM
    target.classList.add("ui-input-video-container")

    // CLEAR CONTENT
    target.replaceChildren();

    // SET INPUT IDs
    let id = this.randomString()
    if (data.id) id = data.id
    let id_preview = this.randomString()
    let id_empty_block = this.randomString()
    let id_label = this.randomString()

    // SET LABEL
    let label = null;
    if (data.label) {
        label = document.createElement("label");
        label.classList.add("ui-input-label");
        label.id = id_label;
        label.htmlFor = id;
        label.textContent = data.label;
        target.append(label);
    }

    // ADD EMPTY BLOCK
    const emptyBlock = document.createElement("div");
    emptyBlock.classList.add("ui-input-video-empty");
    emptyBlock.id = id_empty_block;
    target.append(emptyBlock);

    // ADD VIDEO PREVIEW
    const preview = document.createElement("video");
    preview.classList.add("ui-input-video-preview");
    preview.id = id_preview;
    preview.preload = "auto";
    preview.style.display = "none";
    target.append(preview);

    // ADD CONTROLS
    const controls = document.createElement("div");
    controls.classList.add("ui-input-video-controls");
    controls.style.display = "none";

    const playButton = document.createElement("div");
    playButton.classList.add("ui-input-video-controls-btn", "ui-input-video-controls-btn-play", "glass-icon");
    const playIcon = document.createElement("img");
    playIcon.src = "/images/play-grey.svg";
    playButton.append(playIcon);
    controls.append(playButton);

    const pauseButton = document.createElement("div");
    pauseButton.classList.add("ui-input-video-controls-btn", "ui-input-video-controls-btn-pause", "glass-icon");
    pauseButton.style.display = "none";
    const pauseIcon = document.createElement("img");
    pauseIcon.src = "/images/pause-grey.svg";
    pauseButton.append(pauseIcon);
    controls.append(pauseButton);

    target.append(controls);

    const showPlayButton = () => {
        playButton.style.display = "";
        pauseButton.style.display = "none";
    }

    const showPauseButton = () => {
        playButton.style.display = "none";
        pauseButton.style.display = "";
    }

    const showPreview = () => {
        emptyBlock.style.display = "none";
        preview.style.display = "";
        controls.style.display = "";
        target.style.width = "auto";
        target.style.marginLeft = "auto";
        target.style.marginRight = "auto";
        if (label) label.style.whiteSpace = "nowrap";
    }

    const resetPreview = (value = "removed") => {
        input.type = "hidden";
        input.value = value;
        preview.pause();
        preview.removeAttribute("src");
        preview.replaceChildren();
        preview.load();
        showPlayButton();
        preview.style.display = "none";
        emptyBlock.style.display = "";
        controls.style.display = "none";
        target.style.width = "100%";
        target.style.marginLeft = "";
        target.style.marginRight = "";
        trash.classList.remove("visible");
    }

    const openFileDialog = () => {
        input.type = "file";
        input.value = "";
        input.focus();
        input.click();
    }

    // SET CONTROLS EVENT
    playButton.addEventListener("click", (e)=> {
        e.stopPropagation()
        preview.play()
        showPauseButton();
    })

    pauseButton.addEventListener("click", (e)=> {
        e.stopPropagation()
        preview.pause()
        showPlayButton();
    })

    preview.addEventListener("ended", () => {
        showPlayButton();
    })

    // ADD TRASH
    const trash = document.createElement("div");
    trash.classList.add("ui-input-video-btn-trash");
    target.append(trash);

    // ADD TRASH EVENT
    trash.addEventListener("click", () => {
        resetPreview("removed");
    })

    // ADD INPUT
    const input = document.createElement("input");
    input.type = "hidden";
    input.id = id;
    input.autocomplete = "off";
    input.classList.add("ui-input", "ui-input-video-file");
    if (data.name) input.name = data.name;
    if (this.config.video_types) input.accept = this.config.video_types.join(",");
    input.style.opacity = "0";
    input.style.pointerEvents = "none";
    target.append(input);

    // ADD PREVIEW CLICK EVENT
    preview.addEventListener("click", openFileDialog)

    // ADD EMPTY BLOCK CLICK EVENT
    emptyBlock.addEventListener("click", openFileDialog)

    // ADD INPUT CHANGE EVENT
    input.addEventListener("change", () => {

        // INIT
        const file = input.files[0]
        if (!file) return false;

        preview.src = URL.createObjectURL(file)
        preview.type = file.type
        preview.size = file.size

        // CHECK FILE
        if (!this.config.video_types.includes(file.type)) {
            new Notification({
                title: "Attention",
                content: "Désolé, ce type de fichier n'est pas accepté.",
                icon: "warning.svg"
            });
            resetPreview("");
            return false
        }

        // CHECK SIZE
        if(preview.size > this.config.video_max_size) {
            new Notification({
                title: "Attention",
                content: `Désolé, le poids de la vidéo dépasse le maximum autorisé (${this.config.video_max_size_human}).`,
                icon: "warning.svg"
            });
            resetPreview("");
            return false
        }

        preview.onloadedmetadata = () => {
            const duration = preview.duration

            // CHECK DURATION
            if(duration > this.config.video_max_time) {
                new Notification({
                    title: "Attention",
                    content: `Désolé, la durée de la vidéo dépasse les ${this.config.video_max_time} secondes autorisées.`,
                    icon: "warning.svg"
                });
                resetPreview("");
                return false
            }


            // GET RATIO AND SET SIZE
            const ratio = preview.videoWidth / preview.videoHeight
            if(ratio <= 1) {
                preview.style.height = "250px"
            } else {
                preview.style.height = "150px"
            }

            showPreview();

            // SET TRASH VISIBLE
            trash.classList.add("visible")

        }

    })


    // SET CONTENT IF EXISTS
    if (data.value && data.value.id_medias && data.value.extension) {

        // SET SOURCE
        const url = this.getUploadUrl(`${data.value.id_medias}.${data.value.extension}`)
        const source = document.createElement("source");
        source.src = url;
        source.type = data.value.type;
        preview.append(source);
        preview.load();

        // SET TRASH VISIBLE
        trash.classList.add("visible")

        // GET RATIO AND SET SIZE
        const ratio = data.value.width / data.value.height
        if(ratio <= 1) {
            preview.style.height = "250px"
        } else {
            preview.style.height = "150px"
        }

        showPreview();

        // SET NO CHANGE
        input.type = "hidden";
        input.value = "unchanged";


    } else {

        // SET BTN TRASH INVISIBLE
        trash.classList.remove("visible")

    }

    return input;

}

// MEDIAS
UIInputMediaPhoto(container, medias, mode) {

    new Logs("message", ["FORM BUILDER MEDIAS", container, medias, mode]);

    // ADD CONTAINER ITEM
    const id_item = this.randomString()
    $(container).append(`
        <div class="item medias" id="${id_item}">
            <label class="ui-input-label">Ajouter une ou plusieurs photos</label>
        </div>`)
    container = `#${id_item}`;


    // ADD PLUS BUTTON
    if (Object.keys(this.edit.medias).length < this.config.upload_max) {
        let content = `<div class="media-plus visible"></div`;
        $(container).append(content);
        $(container + " .media-plus").click(() => {
            this.UIInputMediaPhotoAddInputAndPreviewMedia(container, id_item, medias.name, null, false)
        })
    }

    // CREATE INPUTS FOR UPDATE
    if (mode === "update" && medias.exist_values && medias.exist_values.length > 0) {

        medias.exist_values.forEach((media) => {

            // ADD INPUT AND PREVIEW
            this.UIInputMediaPhotoAddInputAndPreviewMediaExist(container, id_item, media.id_medias, media.extension)

        })
    }

}

// ADD INPUT AND PREVIEW MEDIA
UIInputMediaPhotoAddInputAndPreviewMedia(container, main_id, name, value, mode) {

    new Logs("message", ["ADD INPUT PREVIEW MEDIA", container, main_id, name, value, mode])

    // CHECK COUNT
    const count = Object.keys(this.edit.medias);
    if (count === this.config.upload_max) {
        new Notification({
            title: "Désolé",
            content: "Le nombre maximum d'images est atteint.",
            icon: "warning.svg"
        });
        return false
    }

    // SET ID AND NAME
    let item_id = this.randomString();
    name = name + item_id

    // SET SLOT DATA
    this.edit.medias[item_id] = {
        id: item_id,
        name: name,
        file: null,
        type: null
    };


    const input = this.createInput({
        id: item_id,
        name
    }, {
        type: "file",
        classes: ["media-file"]
    });
    $(container).append(input);
    $(`#${item_id}`).change(() => {
        const reader = new FileReader()
        const file = document.getElementById(item_id).files[0]

        if (this.UIInputMediaPhotoCheckFileType(file.type)) {

            // SET DATA
            this.edit.medias[item_id].type = file.type
            this.edit.medias[item_id].file = file.name

            reader.readAsDataURL(file)
            reader.onload = function () {
                // SET BACKGROUND TO PREVIEW
                $(`#media_${item_id}`).css({"background-image": `url(${this.result})`, "background-size": "cover"})

                // SET TRASH VISIBLE
                $(`#media_${item_id} .media-trash`).addClass("visible")
            };

        }
    })

    // SET VALUE
    if (value) $(`#${item_id}`).prop('value', value)

    // ADD PREVIEW
    $(container + " .media-plus").before(`
            <div class="media-preview" id="media_${item_id}">
                <div class="media-trash visible"></div>
                <div class="media-sphere visible"></div>
            </div>
        `);
    $(`#media_${item_id}`).click(() => {
        $(`#${item_id}`).focus().click();
    })
    $(`#media_${item_id} .media-trash`).click((e) => {
        e.stopPropagation()
        this.UIInputMediaPhotoRemoveElement(main_id, item_id)
    })
    $(`#media_${item_id} .media-sphere`).click((e) => {
        e.stopPropagation()
        $(`#media_${item_id} .media-sphere`).toggleClass("on")
    })

    // PLUS VISIBLE
    if (Object.keys(this.edit.medias).length >= this.config.upload_max) {
        new Logs("warning", ["Upload max slot is reached."])
        $(`#${main_id} .media-plus`).removeClass("visible");
    }


    // FOCUS AND CLICK
    $(`#${item_id}`).focus().click();


}

// ADD INPUT AND PREVIEW MEDIA EXIST
UIInputMediaPhotoAddInputAndPreviewMediaExist(container, main_id, id_medias, extension) {

    new Logs("message", ["ADD INPUT PREVIEW MEDIA EXIST", container, main_id, id_medias, extension])

    // INIT
    const item_id = this.randomString();
    const media_file = id_medias + "." + extension

    // ADD PREVIEW
    $(container + " .media-plus").before(`
            <div class="media-preview no-click" id="media_${item_id}" style="background-image: url('${this.getUploadUrl(media_file)}'); background-size: cover;">
                <div class="media-trash visible"></div>
            </div>
        `);

    $(`#media_${item_id} .media-trash`).click((e) => {
        e.stopPropagation()
        this.UIInputMediaPhotoRemoveElementExist(main_id, item_id, id_medias)
    })

    // PLUS VISIBLE
    if (Object.keys(this.edit.medias).length >= this.config.upload_max) {
        new Logs("warning", ["Upload max slot is reached."])
        $(`#${main_id} .media-plus`).removeClass("visible");
    }

}


// MEDIA - REMOVE ELEMENT
UIInputMediaPhotoRemoveElement(main_id, id) {
    new Logs("message", ["REMOVE INPUT MEDIA", main_id, id]);
    {

        // REMOVE INTO INSERT
        delete this.edit.medias[id]

        // REMOVE INPUT
        $(`#${id}`).remove()

        // REMOVE MEDIA PREVIEW
        $(`#media_${id}`).remove()

        // PLUS VISIBLE
        if (Object.keys(this.edit.medias).length < this.config.upload_max) {
            $(`#${main_id} .media-plus`).addClass("visible");
        }

    }

}

// MEDIA - REMOVE ELEMENT EXIST
UIInputMediaPhotoRemoveElementExist(main_id, id, id_medias) {

    new Logs("message", ["REMOVE INPUT MEDIA EXIST", main_id, id, id_medias]);
    {

        // ADD TO REMOVE LIST
        this.edit.medias_removed.push(id_medias)

        // REMOVE MEDIA PREVIEW
        $(`#media_${id}`).remove()

        // PLUS VISIBLE
        if (Object.keys(this.edit.medias).length < this.config.upload_max) {
            $(`#${main_id} .media-plus`).addClass("visible");
        }

    }

}

// MEDIA - CHECK FILE TYPE
UIInputMediaPhotoCheckFileType(type) {
    new Logs("message", ["CHECK FILE TYPE"]);
    if (!this.config.upload_accepted.includes(type)) {
        new Notification({
            title: "Attention",
            content: this.config.upload_rejected_text,
            icon: "warning.svg"
        });
        return false
    }
    return true
}

// RANGE
UIInputRange(container, data) {

    new Logs("message", ["FORM BUILDER RANGE", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = document.createElement("input");
    input.type = "range";
    input.id = this.getInputId(data);
    input.autocomplete = data.autocomplete || "off";
    input.classList.add("ui-input", "ui-input-range");

    if (data.name) input.name = data.name;
    if (data.disabled) input.disabled = true;
    if (data.readonly) input.readOnly = true;
    if (data.required) input.required = true;
    if (data.min !== null && typeof data.min !== "undefined") input.min = data.min;
    if (data.max !== null && typeof data.max !== "undefined") input.max = data.max;
    if (data.step !== null && typeof data.step !== "undefined") input.step = data.step;

    const value = this.getInputValue(data);
    if (value !== null && typeof value !== "undefined") input.value = value;

    const label = this.appendLabel(target, data, input.id);
    if (label) label.classList.add("ui-input-range-label");
    target.append(input);

    const scale = document.createElement("div");
    scale.classList.add("ui-input-range-scale");
    target.append(scale);

    if (data.show_min) {
        const min = document.createElement("div");
        min.classList.add("ui-input-range-scale-min");
        min.textContent = data.min;
        scale.append(min);
    }

    if (data.show_max) {
        const max = document.createElement("div");
        max.classList.add("ui-input-range-scale-max");
        max.textContent = data.max;
        scale.append(max);
    }

    if (data.show_middle) {
        const middle = document.createElement("div");
        middle.classList.add("ui-input-range-scale-middle");
        middle.textContent = data.middle_value;
        scale.append(middle);
    }

    if (data.show_grad && data.grad_step) {
        for (let i = 0; i < data.grad_step; i++) {
            const grad = document.createElement("div");
            grad.classList.add("ui-input-range-scale-grad");
            scale.append(grad);
        }
    }

    if (data.show_value) {
        const valueInput = document.createElement("input");
        valueInput.type = "number";
        valueInput.id = `${input.id}-value`;
        valueInput.classList.add("ui-input-number","ui-input-range-scale-value");
        valueInput.value = input.value;
        scale.append(valueInput);

        input.addEventListener("mousemove", () => {
            valueInput.value = input.value;
        });

        valueInput.addEventListener("change", () => {
            input.value = valueInput.value;
            if (typeof data.onChange === "function") data.onChange();
        });
    }

    this.bindInputChange(input, data);

    return input;

}

// TEXT
UIInputText(container, data) {

    new Logs("message", ["FORM BUILDER TEXT", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "text"
    });

    this.appendLabel(target, data, input.id);
    target.append(input);
    this.bindInputChange(input, data);

    return input;

}

// LOCATION
UIInputLocation(container, data, mode, id_modal) {

    new Logs("message", ["UI INPUT LOCATION", container, data, mode, id_modal]);

    const target = this.getTarget(container);
    if (!target) return false;

    const itemId = this.getInputId(data);
    this.appendLabel(target, data, itemId);

    const inputX = this.createInput({
        name: `${data.name}_x`,
        id: `${itemId}_x`
    }, {
        type: "hidden"
    });
    const inputY = this.createInput({
        name: `${data.name}_y`,
        id: `${itemId}_y`
    }, {
        type: "hidden"
    });



    const button = document.createElement("button");
    button.type = "button";
    button.classList.add("ui-buttons", "medium");
    button.id = `${itemId}_btn`;
    button.textContent = "Choisir";

    const icon = document.createElement("img");
    icon.src = "/images/locate-full-grey.svg";
    button.prepend(icon);

    target.append(inputX, inputY, button);
    target.classList.add("ui-input-locate-container");

    if (data.value) {
        inputX.value = data.value.x;
        inputY.value = data.value.y;
        button.classList.add("no-icon-filter");
        icon.src = "/images/check.svg";
        button.textContent = "Défini";
        button.prepend(icon);
    }

    button.addEventListener("click", (event) => {
        const clickOptions = {
            event,
            element: this,
            data,
            itemId,
            inputX,
            inputY,
            button
        };

        if (typeof data.onClick === "function") {
            data.onClick(clickOptions);
            return;
        }

        UIFormElement.UIInputLocationDefaultClick(clickOptions);
    });

    return {
        x: inputX,
        y: inputY,
        button
    };

}

static UIInputLocationDefaultClick(options) {

    const { element, data, itemId } = options;

    element.tmp.not_id_locate = new Notification({
        icon: "locate-grey.svg",
        content: "Cliquez sur la carte pour positionner votre point.",
        close: false,
        wait: true
    });

    element.tmp.location_ask = {
        icon: data.icon,
        id: itemId,
        name: data.map_label,
        color: data.color,
        id_modal: element.edit.id_modal
    };

    if (element.drawRando) element.drawRando.exit();
    element.setClickMode("set_location");
    element.UIModalTurn(element.edit.id_modal);

}

// NUMBER
UIInputNumber(container, data) {

    new Logs("message", ["FORM BUILDER TEXT", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "number",
        classes: ["ui-input-number"]
    });

    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// URL
UIInputUrl(container, data) {

    new Logs("message", ["FORM BUILDER URL", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "url",
        classes: []
    });

    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// PASSWORD
UIInputPassword(container, data) {

    new Logs("message", ["FORM BUILDER PASSWORD", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "password",
        classes: ["ui-input-password"]
    });

    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// CHECKBOX
UIInputCheckbox(container, data) {

    new Logs("message", ["FORM BUILDER CHECKBOX", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const itemId = this.getInputId(data);
    const checkboxId = `check_${itemId}`;

    this.appendLabel(target, data, itemId);

    const wrapper = document.createElement("div");
    wrapper.classList.add("ui-input","ui-input-checkbox");

    const button = document.createElement("div");
    button.classList.add("ui-input-checkbox-slidebutton");
    button.id = checkboxId;

    const slider = document.createElement("div");
    slider.classList.add("ui-input-checkbox-slider");
    button.append(slider);

    const input = this.createInput({
        ...data,
        id: itemId
    }, {
        type: "hidden"
    });

    wrapper.append(button, input);

    const sideTextValue = this.getCheckboxSideText(data, Boolean(data.value));
    let sideText = null;
    if (sideTextValue) {
        sideText = document.createElement("div");
        sideText.classList.add("ui-input-checkbox-sidetext");
        sideText.textContent = sideTextValue;
        wrapper.append(sideText);
    }

    target.append(wrapper);

    if (data.value) {
        wrapper.classList.add("ui-input-checkbox-on");
        input.value = data.value;
    }

    if (data.value === 0) input.value = 0;

    button.addEventListener("click", () => {
        wrapper.classList.toggle("ui-input-checkbox-on");

        if (wrapper.classList.contains("ui-input-checkbox-on")) {
            input.value = data.values;
        } else {
            input.value = data.value_off || "";
        }

        if (sideText) {
            sideText.textContent = this.getCheckboxSideText(data, wrapper.classList.contains("ui-input-checkbox-on"));
        }

        if (data.onChange) data.onChange();
    });

    return input;

}

// TEL
UIInputTel(container, data) {

    new Logs("message", ["FORM BUILDER TEL", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "tel",
        classes: ["ui-input-tel"]
    });

    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// EMAIL
UIInputEmail(container, data) {

    new Logs("message", ["FORM BUILDER EMAIL", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "email",
        classes: ["ui-input-email"]
    });

    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// VALIDATION CODE
UIInputValidationCode(container, data) {

    new Logs("message", ["FORM BUILDER VALIDATION CODE", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const inputId = this.getInputId(data);
    const count = Number.isInteger(data.count) ? data.count : Number(data.fields || data.length || 6);
    const format = Array.isArray(data.format) && data.format.length > 0
        ? data.format
        : Array.from({length: count}, () => "text");
    const wrapper = document.createElement("div");
    const hiddenInput = this.createInput(data, {
        type: "hidden",
        id: inputId
    });
    const visibleInputs = [];

    target.classList.add("ui-input-validation-code-container");
    wrapper.classList.add("ui-input-validation-code");

    this.appendLabel(target, data, inputId);

    for (let index = 0; index < count; index += 1) {
        const kind = format[index] || "text";
        const input = document.createElement("input");

        input.type = "text";
        input.classList.add("ui-input", "ui-input-validation-code-character");
        input.id = `${inputId}_${index}`;
        input.maxLength = 1;
        input.autocomplete = "one-time-code";
        input.required = !!data.required;
        input.inputMode = kind === "integer" ? "numeric" : "text";
        input.pattern = kind === "integer" ? "[0-9]" : kind === "letter" ? "[A-Za-z]" : ".";
        input.dataset.validationCodeIndex = String(index);
        input.dataset.validationCodeKind = kind;

        input.addEventListener("input", () => {
            input.value = this.normalizeValidationCodeCharacter(input.value, kind);
            this.updateValidationCodeValue(hiddenInput, visibleInputs);

            if (input.value && visibleInputs[index + 1]) {
                visibleInputs[index + 1].focus();
                visibleInputs[index + 1].select();
            }
        });

        input.addEventListener("keydown", (event) => {
            if (event.key === "Backspace" && !input.value && visibleInputs[index - 1]) {
                visibleInputs[index - 1].focus();
                visibleInputs[index - 1].select();
            }
        });

        input.addEventListener("paste", (event) => {
            event.preventDefault();
            this.fillValidationCodeFromPaste(event.clipboardData?.getData("text") || "", visibleInputs, format, index);
            this.updateValidationCodeValue(hiddenInput, visibleInputs);
        });

        this.bindValueState(input);
        visibleInputs.push(input);
        wrapper.append(input);
    }

    const value = this.getInputValue(data);
    if (value) {
        this.fillValidationCodeFromPaste(String(value), visibleInputs, format, 0);
        this.updateValidationCodeValue(hiddenInput, visibleInputs);
    }

    target.append(hiddenInput, wrapper);

    return hiddenInput;

}

normalizeValidationCodeCharacter(value, kind) {

    new Logs("message", ["FORM BUILDER NORMALIZE VALIDATION CODE CHARACTER", value, kind]);

    const character = String(value || "").slice(-1);

    if (kind === "integer") {
        return character.replace(/\D/g, "");
    }

    if (kind === "letter") {
        return character.replace(/[^a-z]/i, "");
    }

    return character;

}

fillValidationCodeFromPaste(value, inputs, format, startIndex = 0) {

    new Logs("message", ["FORM BUILDER FILL VALIDATION CODE FROM PASTE", value, startIndex]);

    let writeIndex = startIndex;

    String(value || "").split("").forEach((character) => {
        if (!inputs[writeIndex]) return;

        const normalized = this.normalizeValidationCodeCharacter(character, format[writeIndex] || "text");

        if (!normalized) return;

        inputs[writeIndex].value = normalized;
        writeIndex += 1;
    });

    const focusInput = inputs[Math.min(writeIndex, inputs.length - 1)];
    focusInput?.focus();
    focusInput?.select();

}

updateValidationCodeValue(hiddenInput, inputs) {

    new Logs("message", ["FORM BUILDER UPDATE VALIDATION CODE VALUE"]);

    hiddenInput.value = inputs.map((input) => input.value).join("");
    hiddenInput.dispatchEvent(new Event("change", {bubbles: true}));

}

// DATE
UIInputDate(container, data) {

    new Logs("message", ["FORM BUILDER DATE", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "date",
        classes: ["ui-input-date"]
    });

    if (data.value === "NOW") {
        input.value = new moment().format("YYYY-MM-DD");
    } else if (data.value) {
        input.value = new moment(data.value).format("YYYY-MM-DD");
    }
    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// TIME
UIInputTime(container, data) {

    new Logs("message", ["FORM BUILDER TIME", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    const input = this.createInput(data, {
        type: "time",
        classes: ["ui-input-time"]
    });

    if (data.value === "NOW") {
        input.value = new moment().format("HH:mm");
    }
    this.appendLabel(target, data, input.id);
    target.append(input);
    this.appendUnit(target, data);
    this.bindInputChange(input, data);

    return input;

}

// SELECT
UIInputSelect(container, data) {

    new Logs("message", ["FORM BUILDER SELECT", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    // SET ID
    let item_id = this.randomString();
    if (data.id) {
        item_id = data.id
    }

    // INIT
    let maker_array = ""
    if (data.multiple) {
        maker_array = "[]"
    }

    // SET LABEL
    if (data.label) {
        const label = document.createElement("label");
        label.classList.add("ui-input-label");
        label.htmlFor = item_id;
        label.textContent = data.label;
        target.append(label);
    }

    // SET SELECT
    const select = document.createElement("select");
    select.id = item_id;
    select.classList.add("ui-select")
    select.autocomplete = "off";
    if (data.name) select.name = `${data.name}${maker_array}`;
    if (data.multiple) select.multiple = true;
    target.append(select);

    // SET PROPS
    if (data.disabled) select.disabled = true
    if (data.readonly) select.readOnly = true
    if (data.required) select.required = true

    // CHECK VALUES CONFIG
    if (!data.values_config) {
        new Logs("warning", ["Values Config is not defined for this select, try to use default values (label, value)"])
        data.values_config = {
            "label": "label",
            "value": "value"
        }
    }

    // CHECK VALUES DATA
    if (!data.values || data.values.length === 0) {
        new Logs("warning", ["Values are null or not defined, stop here."])
        return false;
    }

    // CHECK VALUES WITH VALUES CONFIG
    if (data.values_config) {
        data.values.forEach((item) => {
            // CHECK LABEL
            if (!item[data.values_config.label]) {
                new Logs("error", ["this item as no label, stop here", item])
            }

            // CHECK VALUE
            if (item[data.values_config.value] === "undefined") {
                new Logs("error", ["this item as no value, stop here", item])
            }

        })
    }

    // SET VALUES
    data.values.forEach((item) => {
        const option = document.createElement("option");
        option.value = item[data.values_config.value];
        option.classList.add("ui-input-select-option");
        option.textContent = item[data.values_config.label];
        select.append(option);
    })

    // ADD CHOOSER VALUE IF NOT MULTIPLE
    if (!data.multiple) {
        const chooser = document.createElement("option");
        chooser.value = "";
        chooser.textContent = "Choisissez...";
        select.prepend(chooser);
    }

    // SET VALUE
    if (data.value) {
        select.value = data.value
    } else {
        select.value = ""
    }

    // EVENT ON CHANGE
    if (data.onChange) {
        select.addEventListener("change", (event) => {
            data.onChange(event)
        })
    }

    this.bindValueState(select);
    return select;

}

// SELECT MULTI
UIInputSelectMulti(container, options, subtitem_id) {

    new Logs("message", ["UI INPUT SELECT MULTI", container, options, subtitem_id]);

    // INIT
    const target = this.getTarget(container);
    if (!target) return false;

    const name = options.name
    const name_selector = "selector-" + name
    const list_id = this.randomString()

    // SET ID
    let select_id = this.randomString()
    let selector_id = this.randomString()
    if (options.id) {
        select_id = options.id
        selector_id = "selector-" + options.id
    }

    // SET LABEL
    if (options.label) {
        const label = document.createElement("label");
        label.classList.add("ui-input-label");
        label.htmlFor = select_id;
        label.textContent = options.label;
        target.append(label);
    }

    // SET SELECT FOR SELECTION
    const selector = document.createElement("select");
    selector.name = name_selector;
    selector.id = selector_id;
    selector.autocomplete = "off";
    selector.classList.add("ui-select","ui-select-multi-selector");
    target.append(selector);

    // SET SELECT FOR DATA
    const select = document.createElement("select");
    select.name = `${options.name}[]`;
    select.id = select_id;
    select.multiple = true;
    select.autocomplete = "off";
    select.classList.add("ui-select-multi-select");
    target.append(select);

    // SET PROPS
    if (options.disabled) select.disabled = true
    if (options.readonly) select.readOnly = true
    if (options.required) select.required = true

    const appendOption = (value, label) => {
        const dataOption = document.createElement("option");
        dataOption.value = value;
        dataOption.textContent = label;
        select.append(dataOption);

        const selectorOption = document.createElement("option");
        selectorOption.value = value;
        selectorOption.textContent = label;
        selector.append(selectorOption);
    }

    // SET OPTIONS
    if (Array.isArray(options.values)) {
        // CHECK VALUES CONFIG
        if (!options.values_config) {
            new Logs("error", ["Values Config is not defined for this select."])
            return false;
        }

        // CHECK VALUES DATA
        if (options.values.length === 0) {
            new Logs("warning", ["Values are null or not defined, stop here."])
            return false;
        }

        // SET OPTIONS
        options.values.forEach((item) => {

            let label = item[options.values_config.label]
            const value = item[options.values_config.value]

            // CHECK IF LABEL IS OBJECT
            if (typeof options.values_config.label === "object") {
                label = item[options.values_config.label.label_property][options.values_config.label.label_value]
            }

            // SET OPTIONS
            appendOption(value, label);

        })

    } else {
        new Logs("warning", ["Values are null or not defined, stop here."])
        return false;

    }

    // ADD CHOOSER VALUE
    const chooser = document.createElement("option");
    chooser.value = "";
    chooser.textContent = "Choisissez...";
    selector.prepend(chooser);

    // ADD LIST
    const listContainer = document.getElementById(subtitem_id) || target;
    const list = document.createElement("div");
    list.id = list_id;
    list.classList.add("ui-select-multi-list");
    listContainer.append(list);

    // SET VALUE
    if (options.value && options.value.length > 0) {
        options.value.forEach((val) => {
            const value = typeof val === "object"
                ? val[options.values_config.value]
                : val;

            const selectedOption = Array.from(select.options).find((option) => option.value === String(value));
            if (selectedOption) selectedOption.selected = true;
        })
    }

    // INIT SELECT VALUE
    selector.value = "";

    // SET EVENTS
    selector.addEventListener("change", () => {
        const value_selected = selector.value
        const selectedOption = Array.from(select.options).find((option) => option.value === value_selected);

        if (selectedOption) selectedOption.selected = true;

        selector.value = "";
        this.UIInputSelectMultiRender(select_id, selector_id, list_id)
    })
    this.bindValueState(selector);

    // RENDER LIST
    setTimeout(() => {
        this.UIInputSelectMultiRender(select_id, selector_id, list_id)
    }, 100)

}

// SELECT MULTI RENDER
UIInputSelectMultiRender(select_id, selector_id, list_id) {

    new Logs("message", ["UI INPUT SELECT MULTI RENDER", select_id, selector_id, list_id]);

    // INIT
    const select = document.getElementById(select_id)
    const list = document.getElementById(list_id)
    if (!select || !list) return false;

    const data = Array.from(select.options).filter((option) => option.selected);

    // EMPTY LIST
    list.replaceChildren();

    // RENDER LIST
    data.forEach((option) => {

        const value = option.value;
        const label = option.textContent;
        const id = this.randomString(6)

        const item = document.createElement("div");
        item.id = id;
        item.classList.add("ui-select-multi-list-item");

        const itemLabel = document.createElement("div");
        itemLabel.classList.add("ui-select-multi-list-item-label");
        itemLabel.textContent = label;
        item.append(itemLabel);

        const itemButton = document.createElement("div");
        itemButton.classList.add("ui-select-multi-list-item-button", "glass-icon");
        item.append(itemButton);

        list.append(item);

        // EVENT ON BUTTON
        itemButton.addEventListener("click", () => {
            option.selected = false;
            this.UIInputSelectMultiRender(select_id, selector_id, list_id)
        });

    });


}

// TEXTAREA
UIInputTextArea(container, data) {

    new Logs("message", ["FORM BUILDER TEXTAREA", data, container]);

    const target = this.getTarget(container);
    if (!target) return false;

    // SET ID
    let item_id = this.randomString();
    if (data.id) {
        item_id = data.id
    }

    // SET LABEL
    if (data.label) {
        const label = document.createElement("label");
        label.classList.add("ui-input-label");
        label.htmlFor = item_id;
        label.textContent = data.label;
        target.append(label);
    }

    // SET TEXTAREA
    const textarea = document.createElement("textarea");
    textarea.id = item_id;
    textarea.classList.add("ui-textarea")
    textarea.autocomplete = data.autocomplete ? "on" : "off";

    if (data.name) textarea.name = data.name;
    if (data.disabled) textarea.disabled = true;
    if (data.readonly) textarea.readOnly = true;
    if (data.required) textarea.required = true;
    if (data.class) textarea.classList.add(...data.class.split(" ").filter(Boolean));

    // SET VALUE
    if (data.value !== null && typeof data.value !== "undefined") {
        textarea.value = data.value;
    }

    target.append(textarea);
    this.bindValueState(textarea);

    return textarea;

}

}

globalThis.UIFormElement = UIFormElement;
